import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import { fetchPendingCount } from '../../lib/admin.js';

export default function Navbar() {
  const { t } = useTranslation();
  const { isSignedIn, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 40);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Poll pending submissions for admins so the badge stays roughly current
  // without needing realtime subscriptions.
  useEffect(() => {
    if (!isAdmin) {
      setPendingCount(0);
      return undefined;
    }
    let cancelled = false;
    let timer;
    async function tick() {
      const n = await fetchPendingCount();
      if (!cancelled) setPendingCount(n);
      timer = setTimeout(tick, 60_000);
    }
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isAdmin]);

  const navItems = [
    { to: '/about', label: t('nav.about') },
    { to: '/survey', label: t('nav.survey') },
    { to: '/explore', label: t('nav.explore') },
    { to: '/newsletter', label: t('nav.newsletter') },
    { to: '/help', label: t('nav.help') },
  ];

  // The actual contribute actions live on each resource page ("Add entry"
  // buttons). A standalone Contribute nav link just routed signed-in
  // users back through /auth, so we drop it; signed-out visitors still
  // land on /auth via the homepage CTA and the Register nav link.
  if (isSignedIn) {
    if (isAdmin) navItems.push({ to: '/admin', label: t('nav.admin'), badge: pendingCount });
    navItems.push({ to: '/profile', label: t('nav.profile') });
  } else {
    navItems.push({ to: '/auth', label: t('nav.register') });
  }

  function closeMenu() {
    setOpen(false);
  }

  async function handleSignOut() {
    closeMenu();
    await signOut();
    navigate('/');
  }

  return (
    <nav className={`nav-shell ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="shell flex items-center justify-between px-8 py-3.5">
        <Link
          to="/"
          className="font-serif text-[1.35rem] font-semibold tracking-[0.04em] text-cream"
          onClick={closeMenu}
        >
          Hermeneutics <span className="text-gold">Commons</span>
        </Link>

        <ul className="hidden items-center gap-8 md:flex">
          {navItems.map((item) => (
            <li key={`${item.to}-${item.label}`}>
              <NavLink to={item.to} className="nav-link" onClick={closeMenu}>
                {item.label}
                {item.badge > 0 && (
                  <span className="ml-1.5 rounded-full border border-gold/40 bg-navy-mid/40 px-1.5 text-[0.65rem] text-gold-light">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
          {isSignedIn && (
            <li>
              <button type="button" className="nav-link" onClick={handleSignOut}>
                {t('nav.logout')}
              </button>
            </li>
          )}
        </ul>

        <div className="flex items-center gap-2">
          <LanguageSelector />
          <button
            type="button"
            className="ml-1 flex flex-col gap-[5px] p-1 md:hidden"
            aria-label={t('nav.menu')}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            <span className="block h-0.5 w-6 bg-ink-secondary transition" />
            <span className="block h-0.5 w-6 bg-ink-secondary transition" />
            <span className="block h-0.5 w-6 bg-ink-secondary transition" />
          </button>
        </div>
      </div>

      {open && (
        <div
          className="absolute inset-x-0 top-full flex flex-col gap-5 border-b px-8 py-6 md:hidden"
          style={{
            background: 'rgba(10, 22, 40, 0.97)',
            borderBottomColor: 'rgba(59, 125, 216, 0.12)',
          }}
        >
          {navItems.map((item) => (
            <NavLink
              key={`${item.to}-${item.label}`}
              to={item.to}
              className="nav-link"
              onClick={closeMenu}
            >
              {item.label}
            </NavLink>
          ))}
          {isSignedIn && (
            <button type="button" className="nav-link text-left" onClick={handleSignOut}>
              {t('nav.logout')}
            </button>
          )}
        </div>
      )}
    </nav>
  );
}
